import { TagTypes } from "../../../Api/Types/tagTypes";
import { cleanParams } from "../../Shared/Helpers/cleanParams";
import { Booking, BookingExpenseInvoiceMappingDetailItem } from '../../Shared/Types/bookingTypes';
import { PagedList } from '../../Shared/Types/common';
import {
  AutomaticBookings,
  BookingFilterSelectDataDto,
  BookingListRequest,
} from '../Types/booking.types';
import {
  BookingSearchListItem,
  BookingSearchV2Request,
} from '../Types/bookingSearchTypes';
import { bookingsApi } from "./bookingsApi";
import {
  buildCancelBookingRequest,
  getCancelBookingInvalidatesTags,
} from './bookingCancellationApi.contract';
import {
  buildCreateBookingSimplifiedCostRequest,
  getCreateBookingSimplifiedCostInvalidatesTags,
  handleCreateBookingSimplifiedCostQueryLifecycle,
  type CreateBookingSimplifiedCostMutationArgs,
} from './bookingSimplifiedCostApi.contract';
import type { BatchInvoiceConfig } from '../Types/batchInvoiceConfigTypes';
import {
  mapExpenseInvoiceSuggestionsForBookingResponse,
  type BookingExpenseInvoiceSuggestionLookup,
} from './expenseInvoicesLookupMapper';
import type { ExpenseInvoiceSuggestionForBookingResponseItem } from './expenseInvoicesLookupApi.contract';

export interface BatchInvoiceCreationResultDto {
  reservationId: number;
  success: boolean;
  invoiceId: number | null;
  error: string | null;
}

export interface BatchCreateInvoicesForBookingArgs extends Partial<BatchInvoiceConfig> {
  bookingId: number;
  reservationIds: number[];
}

export const buildBatchCreateInvoicesForBookingRequest = ({
  bookingId,
  reservationIds,
  vatType,
  itemDescriptionOverride,
  invoiceDate,
}: BatchCreateInvoicesForBookingArgs) => ({
  url: `/bookings/${bookingId}/batch-create-invoices`,
  method: 'POST',
  body: { reservationIds, vatType, itemDescriptionOverride, invoiceDate },
});

export const extendedBookingsApi = bookingsApi.injectEndpoints({
  endpoints: (builder) => ({
    createAutomaticBookings: builder.mutation<void, AutomaticBookings>({
      query: (automaticBookingData) => ({
        url: '/bookings/automatic',
        method: 'POST',
        body: automaticBookingData,
      }),
      invalidatesTags: [TagTypes.Booking],
    }),

    // GET: Pobieranie rezerwacji po ID (z rabatami)
    getBookingWithDiscounts: builder.query<Booking, number>({
      query: (id) => `/bookings/with-discounts/${id}`,
      providesTags: (result, error, id) => [{ type: TagTypes.Booking, id }],
    }),

    // GET: Pobieranie rezerwacji po ID (z FV kosztowymi)
    getBookingWithExpenseInvoices: builder.query<Booking, number>({
      query: (id) => `/bookings/with-expense-invoices/${id}`,
      providesTags: (result, error, id) => [{ type: TagTypes.Booking, id }],
    }),

    // GET: Pobieranie rezerwacji po ID (z rezerwacjami)
    getBookingWithReservations: builder.query<Booking, number>({
      query: (id) => `/bookings/with-reservations/${id}`,
      providesTags: (result, error, id) => [{ type: TagTypes.Booking, id }],
    }),

    // GET: Pobieranie rezerwacji dla konkretnej oferty
    getBookingsByOfferId: builder.query<Booking[], number>({
      query: (offerId) => `/bookings/offer/${offerId}`,
      providesTags: (result, error, offerId) => [
        { type: TagTypes.Booking, id: `offer-${offerId}` },
        ...(result ?? []).map((booking) => ({ type: TagTypes.Booking, id: booking.id })),
      ],
    }),
    getBookingFilterSelects: builder.query<BookingFilterSelectDataDto, void>({
      query: () => ({
        url: '/bookings/filters',
        method: 'GET',
      }),
    }),
    getBookingList: builder.query<PagedList<Booking>, BookingListRequest>({
      query: (params) => ({
        url: '/bookings/search',
        params: cleanParams(params),
      }),
      providesTags: [{ type: TagTypes.Booking, id: TagTypes.List }],
    }),
    getBookingSearchV2: builder.query<PagedList<BookingSearchListItem>, BookingSearchV2Request>({
      query: (params) => ({
        url: '/bookings/search-v2',
        params: cleanParams(params),
      }),
      providesTags: [{ type: TagTypes.Booking, id: TagTypes.List }],
    }),
    partialUpdateBooking: builder.mutation<
      void,
      Partial<
        Pick<
          Booking,
          | 'id'
          | 'numberOfParticipants'
          | 'price'
          | 'published'
          | 'possibilityOfReservation'
          | 'bookingDateFrom'
          | 'bookingDateTo'
        >
      >
    >({
      query: ({ id, ...patch }) => ({
        url: `/bookings/${id}`,
        method: 'PATCH',
        body: { id, ...patch },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TagTypes.Booking, id },
        { type: TagTypes.Booking, id: TagTypes.List },
      ],
    }),
    assignDiscountsToBooking: builder.mutation<void, { bookingId: number; discountIds: number[] }>({
      query: ({ bookingId, discountIds }) => ({
        url: `/bookings/${bookingId}/assign-discounts`,
        method: 'PUT',
        body: discountIds,
      }),
      invalidatesTags: (res, err, { bookingId }) => [{ type: TagTypes.Booking, id: bookingId }],
    }),
    createBookingSimplifiedCost: builder.mutation<unknown, CreateBookingSimplifiedCostMutationArgs>({
      query: buildCreateBookingSimplifiedCostRequest,
      invalidatesTags: (result, error, { bookingId }) =>
        getCreateBookingSimplifiedCostInvalidatesTags(bookingId),
      onQueryStarted: ({ bookingId }, lifecycleApi) =>
        handleCreateBookingSimplifiedCostQueryLifecycle(bookingId, lifecycleApi),
    }),

    updateBookingExpenseInvoiceMappingDetails: builder.mutation<
      void,
      { bookingId: number; items: BookingExpenseInvoiceMappingDetailItem[] }
    >({
      query: ({ bookingId, items }) => ({
        url: `/bookings/${bookingId}/expense-invoice-mapping-details`,
        method: 'PUT',
        body: items,
      }),
      invalidatesTags: (result, error, { bookingId }) => [
        { type: TagTypes.Booking, id: bookingId },
      ],
    }),

    cancelBooking: builder.mutation<void, number>({
      query: buildCancelBookingRequest,
      invalidatesTags: (result, error, bookingId) => getCancelBookingInvalidatesTags(bookingId),
    }),

    batchCreateInvoicesForBooking: builder.mutation<
      BatchInvoiceCreationResultDto[],
      BatchCreateInvoicesForBookingArgs
    >({
      query: buildBatchCreateInvoicesForBookingRequest,
      invalidatesTags: [{ type: TagTypes.Invoice, id: 'List' }],
    }),

    // GET: Ranked, explainable, completely-unassigned expense invoice suggestions for a booking.
    // Read-only: selecting a suggestion never persists an assignment by itself.
    getExpenseInvoiceSuggestionsForBooking: builder.query<BookingExpenseInvoiceSuggestionLookup[], number>({
      query: (bookingId) => ({
        url: `/bookings/${bookingId}/expense-invoice-suggestions`,
        method: 'GET',
      }),
      transformResponse: (response: ExpenseInvoiceSuggestionForBookingResponseItem[]) =>
        mapExpenseInvoiceSuggestionsForBookingResponse(response),
      providesTags: (result, error, bookingId) => [{ type: TagTypes.Booking, id: bookingId }],
    }),
  }),
});

export const {
  useCreateAutomaticBookingsMutation,
  useGetBookingWithDiscountsQuery,
  useGetBookingWithExpenseInvoicesQuery,
  useGetBookingWithReservationsQuery,
  useGetBookingsByOfferIdQuery,
  useGetBookingFilterSelectsQuery,
  useGetBookingListQuery,
  useGetBookingSearchV2Query: useGetBookingSearchV2ListQuery,
  usePartialUpdateBookingMutation,
  useAssignDiscountsToBookingMutation,
  useCreateBookingSimplifiedCostMutation,
  useUpdateBookingExpenseInvoiceMappingDetailsMutation,
  useBatchCreateInvoicesForBookingMutation,
  useCancelBookingMutation,
  useGetExpenseInvoiceSuggestionsForBookingQuery: useBookingExpenseInvoiceSuggestionsQuery,
} = extendedBookingsApi;
